/**
 * How two chairs at one table relate — "next to each other", "across", "one seat
 * apart" — derived from the table's real geometry (see lib/seatingGeometry.ts),
 * so a host's "seat couples across from each other" means what it means on the
 * screen they are looking at.
 *
 * The geometry, in one paragraph. A chair is a 0-based ordinal, and the ordinals
 * walk the table in one fixed direction:
 *
 *  - ROUND: clockwise from 12 o'clock. Neighbours are i±1 (wrapping); "one seat
 *    apart" is i±2; "across" is i + N/2 — which only exists when N is EVEN. An
 *    odd table has no exact opposite; the two nearest (i + (N±1)/2) are the
 *    closest it can get.
 *  - RECTANGLE / SQUARE: the perimeter walk — top left-to-right, right head
 *    top-to-bottom, bottom right-to-left, left head bottom-to-top. Neighbours are
 *    i±1 ALONG ONE SIDE (the last chair on the top and the first on the right
 *    head are round a corner, not side by side); "across" is the chair on the
 *    opposite side at the same position along it, which is exact only when the
 *    two sides have the same number of chairs.
 *
 * The planner never fails a seating over a pattern. {@link partnerSeats} says
 * which chairs satisfy a pattern for a given chair, and whether that is the
 * pattern exactly or only the nearest the table shape allows — the caller falls
 * back down the ladder (exact, nearest, beside, anywhere) and says so.
 */
import { computeTableGeometry, type SeatSide } from './seatingGeometry';
import type { SeatingTable } from '../types/seating';

/** What the host picks for pairs. */
export type SeatPattern = 'side_by_side' | 'across' | 'one_apart' | 'anywhere';

export const SEAT_PATTERN_LABELS: Readonly<Record<SeatPattern, string>> = {
  side_by_side: 'Side by side',
  across: 'Across from each other',
  one_apart: 'One seat apart',
  anywhere: 'Anywhere at the table',
};

/** across beats beside beats gap; 'other' is anything else, including the same chair. */
export type SeatRelation = 'beside' | 'gap' | 'across' | 'other';

interface Layout {
  n: number;
  round: boolean;
  sides: SeatSide[];
  /** Seat ordinals on each side of a rectangle, in walk order. */
  bySide: Map<SeatSide, number[]>;
  /** Position along the side, in the shared axis of a facing pair (x for top/bottom, y for left/right). */
  along: number[];
}

function layoutOf(table: Partial<SeatingTable>): Layout {
  const geometry = computeTableGeometry(table);
  const sides = geometry.seats.map((seat) => seat.side);
  const bySide = new Map<SeatSide, number[]>();
  sides.forEach((side, index) => bySide.set(side, [...(bySide.get(side) ?? []), index]));
  const along = geometry.seats.map((seat) => (seat.side === 'left' || seat.side === 'right' ? seat.y : seat.x));
  return { n: geometry.seats.length, round: geometry.shape === 'round', sides, bySide, along };
}

const ringDistance = (n: number, a: number, b: number) => {
  const d = Math.abs(a - b);
  return Math.min(d, n - d);
};

const opposite: Record<SeatSide, SeatSide | null> = {
  round: null,
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/** Chairs next to `seat` along its own side (rectangles) or round the ring (round). */
function beside(layout: Layout, seat: number): number[] {
  const out: number[] = [];
  for (const other of [seat - 1, seat + 1]) {
    if (layout.round) {
      const wrapped = (other + layout.n) % layout.n;
      if (wrapped !== seat && !out.includes(wrapped)) out.push(wrapped);
    } else if (other >= 0 && other < layout.n && layout.sides[other] === layout.sides[seat]) {
      out.push(other);
    }
  }
  return out;
}

function gap(layout: Layout, seat: number): number[] {
  const out: number[] = [];
  for (const other of [seat - 2, seat + 2]) {
    if (layout.round) {
      const wrapped = (other + layout.n) % layout.n;
      if (layout.n >= 4 && ringDistance(layout.n, seat, wrapped) === 2 && !out.includes(wrapped)) out.push(wrapped);
    } else if (other >= 0 && other < layout.n && layout.sides[other] === layout.sides[seat]) {
      out.push(other);
    }
  }
  return out;
}

/** The exact opposite chair, if the shape has one. */
function exactAcross(layout: Layout, seat: number): number | null {
  if (layout.round) {
    return layout.n % 2 === 0 && layout.n >= 2 ? (seat + layout.n / 2) % layout.n : null;
  }
  const side = layout.sides[seat];
  const facing = opposite[side];
  const there = facing ? layout.bySide.get(facing) : undefined;
  const here = layout.bySide.get(side);
  if (!there || !here || there.length !== here.length) return null;
  // Same count: the facing chair is the one at the same place along the side.
  return there.reduce((best, candidate) =>
    Math.abs(layout.along[candidate] - layout.along[seat]) < Math.abs(layout.along[best] - layout.along[seat])
      ? candidate
      : best,
  );
}

/** The closest thing to across: the two near-opposite chairs of an odd ring, or the nearest chair on the facing side. */
function nearAcross(layout: Layout, seat: number): number[] {
  if (layout.round) {
    if (layout.n < 3) return [];
    const low = seat + Math.floor(layout.n / 2);
    const high = seat + Math.ceil(layout.n / 2);
    return [...new Set([low % layout.n, high % layout.n])].filter((other) => other !== seat);
  }
  const facing = opposite[layout.sides[seat]];
  const there = facing ? layout.bySide.get(facing) : undefined;
  if (!there || there.length === 0) return [];
  const distance = (candidate: number) => Math.abs(layout.along[candidate] - layout.along[seat]);
  const best = Math.min(...there.map(distance));
  return there.filter((candidate) => distance(candidate) - best < 1e-6);
}

export function seatRelation(table: Partial<SeatingTable>, a: number, b: number): SeatRelation {
  const layout = layoutOf(table);
  if (a === b || a < 0 || b < 0 || a >= layout.n || b >= layout.n) return 'other';
  if (exactAcross(layout, a) === b) return 'across';
  if (beside(layout, a).includes(b)) return 'beside';
  if (gap(layout, a).includes(b)) return 'gap';
  return 'other';
}

export interface PartnerSeats {
  /** The chairs that satisfy the pattern for this chair. */
  seats: number[];
  /** False when the table shape cannot give the pattern and these are the nearest it can. */
  exact: boolean;
}

/**
 * Where a partner may sit given one chair and a pattern. Never empty for a table
 * of two or more chairs: the ladder ends at "anywhere else".
 */
export function partnerSeats(table: Partial<SeatingTable>, seat: number, pattern: SeatPattern): PartnerSeats {
  const layout = layoutOf(table);
  const others = [...Array(layout.n).keys()].filter((index) => index !== seat);
  if (seat < 0 || seat >= layout.n) return { seats: [], exact: false };
  const ladder = (exact: number[], nearest: number[]): PartnerSeats => {
    if (exact.length > 0) return { seats: exact, exact: true };
    if (nearest.length > 0) return { seats: nearest, exact: false };
    const next = beside(layout, seat);
    return { seats: next.length > 0 ? next : others, exact: false };
  };
  switch (pattern) {
    case 'anywhere':
      return { seats: others, exact: true };
    case 'side_by_side': {
      const next = beside(layout, seat);
      return next.length > 0 ? { seats: next, exact: true } : ladder([], []);
    }
    case 'one_apart':
      return ladder(gap(layout, seat), []);
    case 'across': {
      const exact = exactAcross(layout, seat);
      return ladder(exact === null ? [] : [exact], nearAcross(layout, seat));
    }
    default:
      return { seats: others, exact: true };
  }
}

/** Is `pattern` achievable EXACTLY somewhere on this table? (An odd ring has no across; a 3-seat table no gap.) */
export function patternPossible(table: Partial<SeatingTable>, pattern: SeatPattern): boolean {
  const layout = layoutOf(table);
  if (layout.n < 2) return false;
  return [...Array(layout.n).keys()].some((seat) => partnerSeats(table, seat, pattern).exact);
}

/** Runs of consecutive chairs that stay on one side of a rectangle; the whole ring for a round table. */
export function sameSide(table: Partial<SeatingTable>, seats: number[]): boolean {
  const layout = layoutOf(table);
  return layout.round || seats.every((seat) => layout.sides[seat] === layout.sides[seats[0]]);
}
